import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { VERSION_TERMINOS, textoDeTerminos } from '../../estaticas/legal.js';
import { AUTH_DISPONIBLE, pedirEnlaceMagico, rutaPropia, verificarCodigo } from '../../../common/services/auth.js';
import { CaptchaWidget } from '../../../common/components/CaptchaWidget.js';
import { guardarConsentimientosPendientes } from '../consentimientosPendientes.js';
import type { ConsentimientoAGuardar } from '../../../common/services/cuentas.js';

const TEXTO_NEWSLETTER: Readonly<Record<Idioma, string>> = {
  es: 'Quiero recibir la newsletter de nuevos estudios y novedades de la industria (puede incluir información de productos de Sinapser).',
  en: "I want to receive the newsletter about new studies and industry news (may include information about Sinapser's products).",
};

/**
 * Registro y login por código o enlace mágico. Solo el registro pide consentimientos y permite crear cuenta.
 */
function FormularioAcceso({ idioma, volver, registro }: { readonly idioma: Idioma; readonly volver?: string; readonly registro: boolean }) {
  const t = diccionarioDe(idioma).cuentas;
  const [email, setEmail] = useState('');
  const [emailEnviado, setEmailEnviado] = useState('');
  const [errorEnvio, setErrorEnvio] = useState<string | null>(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [quiereNewsletter, setQuiereNewsletter] = useState(false);
  const [estado, setEstado] = useState<'inicial' | 'enviando' | 'enviado' | 'error' | 'sin_terminos'>('inicial');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // MCP-01: `?volver=/oauth/consent?...` para que el enlace mágico devuelva a la pantalla de
  // consentimiento. `pedirEnlaceMagico` solo acepta rutas de esta misma web.

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (registro && !aceptaTerminos) {
      setEstado('sin_terminos');
      return;
    }
    setEstado('enviando');
    setErrorEnvio(null);
    const correo = email.trim();
    // CAPTCHA-01: el widget vive montado más abajo (`CaptchaWidget`), no se pide aquí bajo demanda.
    // `captchaToken` es `null` sin site key configurada, o si el widget aún no ha resuelto; a
    // diferencia de la caja de preguntas, aquí no se bloquea el envío por no tenerlo: Supabase
    // decide si hacía falta (solo si tiene el captcha activado con el mismo proveedor).
    void pedirEnlaceMagico(correo, captchaToken ?? undefined, volver, registro, idioma)
      .then(error => {
        if (error !== null) {
          setErrorEnvio(error);
          setEstado('error');
          return;
        }
        const consentimientos: ConsentimientoAGuardar[] = [
          { tipo: 'terminos', aceptado: true, textoVersion: VERSION_TERMINOS, texto: textoDeTerminos(idioma) },
        ];
        if (quiereNewsletter) {
          consentimientos.push({ tipo: 'newsletter', aceptado: true, textoVersion: VERSION_TERMINOS, texto: TEXTO_NEWSLETTER[idioma] });
        }
        if (registro) guardarConsentimientosPendientes(correo, consentimientos);
        setEmailEnviado(correo);
        setEstado('enviado');
      })
      .catch(() => {
        setEstado('error');
      });
  };

  // Sin las variables de build de Supabase, el enlace de la cabecera ya está oculto, pero quien
  // escriba esta URL a mano no debe ver un formulario que rompería al enviarlo.
  if (!AUTH_DISPONIBLE) {
    return (
      <div className='flex flex-col gap-4 text-center'>
        <p className='m-0 font-(family-name:--text) text-[17px] text-(--ink)'>{t.noDisponible}</p>
      </div>
    );
  }

  if (estado === 'enviado') {
    return <FormularioCodigo idioma={idioma} email={emailEnviado} volver={volver} cambiarCorreo={() => setEstado('inicial')} />;
  }

  return (
    <div>
      <form onSubmit={enviar} className='flex flex-col gap-4'>
        <div className='flex flex-col rounded-xl border border-(--line-strong) bg-(--chat-bg)'>
          <label htmlFor='email' className='sr-only'>
            {t.email}
          </label>
          <input
            id='email'
            type='email'
            required
            value={email}
            onChange={e => {
              setEmail(e.target.value);
            }}
            placeholder={t.email}
            className='border-none bg-transparent px-5 py-4 font-(family-name:--text) text-[16px] text-(--ink) outline-none'
          />
        </div>

        {registro ? (
          <>
            <label className='flex items-start gap-2.5 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
              <input
                type='checkbox'
                required
                checked={aceptaTerminos}
                onChange={e => {
                  setAceptaTerminos(e.target.checked);
                  if (e.target.checked) setEstado('inicial');
                }}
                className='mt-0.5'
              />
              <span>
                {t.terminos.antes}
                <Link to='/terminos' target='_blank' className='text-(--teal-deep) underline'>
                  {t.terminos.enlace}
                </Link>
                {t.terminos.despues}
              </span>
            </label>

            <label className='flex items-start gap-2.5 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
              <input
                type='checkbox'
                checked={quiereNewsletter}
                onChange={e => {
                  setQuiereNewsletter(e.target.checked);
                }}
                className='mt-0.5'
              />
              <span>
                {t.newsletter}
                <span className='mt-1 block text-[12px] text-(--ink-faint)'>{t.newsletterNota}</span>
              </span>
            </label>
          </>
        ) : null}

        <CaptchaWidget onCambio={setCaptchaToken} />

        <button
          type='submit'
          disabled={estado === 'enviando'}
          className='sobre-teal cursor-pointer rounded-full bg-(--teal-deep) px-5 py-3 font-(family-name:--text) text-[15px] disabled:cursor-not-allowed disabled:opacity-40'
        >
          {estado === 'enviando' ? t.enviando : t.enviar}
        </button>

        {estado === 'error' ? (
          <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
            {errorEnvio === 'over_email_send_rate_limit' || errorEnvio === 'over_request_rate_limit'
              ? t.errorLimiteCorreo
              : errorEnvio === 'email_address_not_authorized'
                ? t.errorServicioCorreo
                : errorEnvio === 'user_not_found'
                  ? t.errorCuentaNoExiste
                  : registro
                    ? t.errorEnvio
                    : t.errorLogin}
          </p>
        ) : null}
        {estado === 'sin_terminos' ? (
          <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
            {t.errorSinTerminos}
          </p>
        ) : null}
      </form>
    </div>
  );
}

function FormularioCodigo({
  idioma,
  email,
  volver,
  cambiarCorreo,
}: {
  readonly idioma: Idioma;
  readonly email: string;
  readonly volver?: string;
  readonly cambiarCorreo: () => void;
}) {
  const t = diccionarioDe(idioma).cuentas;
  const navegar = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [estado, setEstado] = useState<'inicial' | 'verificando' | 'error' | 'red'>('inicial');
  const verificar = async (e: FormEvent) => {
    e.preventDefault();
    if (estado === 'verificando' || !/^\d+$/u.test(codigo.trim())) return;
    setEstado('verificando');
    try {
      const error = await verificarCodigo(email, codigo.trim());
      if (error !== null) {
        setEstado(error === 'otp_expired' ? 'error' : 'red');
        return;
      }
      void navegar(rutaPropia(volver) ?? '/mi-cuenta', { replace: true });
    } catch {
      setEstado('red');
    }
  };
  return (
    <div className='flex flex-col gap-4'>
      <p className='m-0 font-(family-name:--text) text-[16px] text-(--ink)'>{t.enviado}</p>
      <p className='m-0 break-all font-(family-name:--text) text-[14px] text-(--ink-soft)'>{email}</p>
      <form onSubmit={e => void verificar(e)} className='flex flex-col gap-4'>
        <label htmlFor='codigo-acceso' className='font-(family-name:--text) text-[14px] text-(--ink-soft)'>
          {t.codigo}
        </label>
        <input
          id='codigo-acceso'
          type='text'
          inputMode='numeric'
          autoComplete='one-time-code'
          pattern='[0-9]+'
          required
          value={codigo}
          onChange={e => setCodigo(e.target.value)}
          disabled={estado === 'verificando'}
          className='rounded-xl border border-(--line-strong) bg-(--chat-bg) px-5 py-4 text-center font-mono text-[24px] tracking-widest text-(--ink)'
        />
        <button
          type='submit'
          disabled={estado === 'verificando' || !/^\d+$/u.test(codigo.trim())}
          className='sobre-teal cursor-pointer rounded-full bg-(--teal-deep) px-5 py-3 font-(family-name:--text) text-[15px] disabled:cursor-not-allowed disabled:opacity-40'
        >
          {estado === 'verificando' ? t.verificando : t.verificarCodigo}
        </button>
        {estado === 'error' || estado === 'red' ? (
          <p role='alert' className='m-0 text-[14px] text-(--ink-soft)'>
            {estado === 'red' ? t.errorVerificacionRed : t.errorCodigo}
          </p>
        ) : null}
      </form>
      <button
        type='button'
        disabled={estado === 'verificando'}
        onClick={cambiarCorreo}
        className='cursor-pointer text-center text-[14px] text-(--teal-deep) underline'
      >
        {t.otroCorreo}
      </button>
    </div>
  );
}

export function FormularioRegistro({ idioma, volver }: { readonly idioma: Idioma; readonly volver?: string }) {
  return <FormularioAcceso idioma={idioma} volver={volver} registro />;
}

export function IniciarSesionPage({ idioma, registro = false }: { readonly idioma: Idioma; readonly registro?: boolean }) {
  const t = diccionarioDe(idioma).cuentas;
  const [params] = useSearchParams();
  return (
    <main className='mx-auto flex w-full max-w-[480px] grow flex-col gap-6 px-6 py-16'>
      <h1 className='m-0 font-(family-name:--serif) text-[28px] font-bold text-(--ink)'>{registro ? t.tituloRegistro : t.titulo}</h1>
      <p className='m-0 font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.explicacion}</p>
      <FormularioAcceso key={registro ? 'registro' : 'login'} idioma={idioma} volver={params.get('volver') ?? undefined} registro={registro} />
      <Link
        to={`${registro ? '/iniciar-sesion' : '/registro'}${params.get('volver') ? `?volver=${encodeURIComponent(params.get('volver')!)}` : ''}`}
        className='text-center font-(family-name:--text) text-[14px] text-(--teal-deep) underline'
      >
        {registro ? t.yaTengoCuenta : t.crearCuenta}
      </Link>
    </main>
  );
}
